import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Shield, Eye, Lock, Database, Cookie, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

export const PrivacyPage: React.FC = () => {
  const { t } = useTranslation();

  const sections = [
    {
      id: 'information-collection',
      icon: Database,
      title: t('privacy.sections.informationCollection.title'),
      content: t('privacy.sections.informationCollection.content')
    },
    {
      id: 'information-use',
      icon: Eye,
      title: t('privacy.sections.informationUse.title'),
      content: t('privacy.sections.informationUse.content')
    },
    {
      id: 'data-security',
      icon: Lock,
      title: t('privacy.sections.dataSecurity.title'),
      content: t('privacy.sections.dataSecurity.content')
    },
    {
      id: 'cookies',
      icon: Cookie,
      title: t('privacy.sections.cookies.title'),
      content: t('privacy.sections.cookies.content')
    },
    {
      id: 'third-party',
      icon: Shield,
      title: t('privacy.sections.thirdParty.title'),
      content: t('privacy.sections.thirdParty.content')
    },
    {
      id: 'contact',
      icon: Mail,
      title: t('privacy.sections.contact.title'),
      content: t('privacy.sections.contact.content')
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>{t('privacy.title')} - {t('header.title')}</title>
        <meta name="description" content={t('privacy.description')} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${window.location.origin}/privacy`} />
      </Helmet>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('privacy.title')}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t('privacy.subtitle')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
            {t('privacy.lastUpdated')}: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Introduction */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {t('privacy.introduction')}
            </p>
          </CardContent>
        </Card>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <Card key={section.id} className="hover:shadow-lg transition-shadow duration-200">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <span className="text-xl font-semibold text-gray-900 dark:text-white">
                      {index + 1}. {section.title}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-gray dark:prose-invert max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: section.content }} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer */}
        <Card className="mt-12">
          <CardContent className="p-8 text-center">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('privacy.questions.title')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('privacy.questions.description')}
            </p>
            <div className="flex justify-center space-x-4">
              <a
                href="mailto:1481983952@qq.com"
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('privacy.questions.contact')}
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
